const STORAGE_PREFIX = 'falcon-planner:gantt-column-widths:';

export type GanttColumnWidths = {
  version: 1;
  widths: Partial<Record<string, number>>;
};

export type GridColumnLayoutMode = 'fill' | 'scroll';

/** Default starting widths for every resizable schedule grid column. */
export const DEFAULT_GANTT_COLUMN_WIDTHS: Readonly<Record<string, number>> = {
  srNo: 96,
  assignSelect: 44,
  text: 480,
  start_date: 120,
  end_date: 120,
  wbs: 60,
  duration: 80,
  actualStart: 120,
  actualFinish: 120,
  actualDuration: 100,
  progress: 90,
  critical: 80,
  taskType: 100,
  locationType: 120,
  predecessors: 180,
  successors: 180,
  unitActivityTracked: 72,
  assignees: 90,
  baselineStart: 120,
  baselineFinish: 120,
  baselineDuration: 80,
  baselineStartVar: 72,
  baselineFinishVar: 72,
  baselineDurationVar: 64,
};

/** Task name absorbs leftover panel space when columns fit (fill mode). */
export const FLEX_GANTT_COLUMN_NAME = 'text';

/**
 * Columns whose widths we persist to sessionStorage when the user resizes.
 * Any name in this set (or any resized name that appears in the live map) is saved.
 */
export const PERSISTED_GANTT_COLUMN_NAMES = [
  'srNo',
  'assignSelect',
  'text',
  'start_date',
  'end_date',
  'wbs',
  'duration',
  'actualStart',
  'actualFinish',
  'actualDuration',
  'progress',
  'critical',
  'taskType',
  'locationType',
  'predecessors',
  'successors',
  'unitActivityTracked',
  'assignees',
  'baselineStart',
  'baselineFinish',
  'baselineDuration',
  'baselineStartVar',
  'baselineFinishVar',
  'baselineDurationVar',
] as const;

/** @deprecated Prefer FLEX_GANTT_COLUMN_NAME + session map; kept for call-site clarity. */
export const PINNED_GANTT_COLUMN_NAMES = ['srNo', 'text'] as const;

/** Slack (px) before enabling grid H-scroll when sum of widths exceeds panel. */
export const GRID_COLUMN_OVERFLOW_SLACK_PX = 8;

function storageKey(projectId: string): string {
  return `${STORAGE_PREFIX}${projectId}`;
}

export function loadGanttColumnWidths(projectId: string): GanttColumnWidths | null {
  if (!projectId || typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(storageKey(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GanttColumnWidths;
    if (!parsed || parsed.version !== 1 || !parsed.widths || typeof parsed.widths !== 'object') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveGanttColumnWidths(
  projectId: string,
  widths: Partial<Record<string, number>>,
): void {
  if (!projectId || typeof window === 'undefined') return;
  const next: GanttColumnWidths = {
    version: 1,
    widths: { ...(loadGanttColumnWidths(projectId)?.widths ?? {}), ...widths },
  };
  try {
    window.sessionStorage.setItem(storageKey(projectId), JSON.stringify(next));
  } catch {
    // ignore quota / private mode
  }
}

/** True when the live map has any positive persisted column width. */
export function hasPinnedGanttColumnWidths(
  widths: Partial<Record<string, number>> | null | undefined,
): boolean {
  if (!widths) return false;
  for (const name of Object.keys(widths)) {
    const w = widths[name];
    if (typeof w === 'number' && w > 0) return true;
  }
  return false;
}

/** True when `name` is a column we persist on resize. */
export function isPersistableGanttColumnName(name: string): boolean {
  return (PERSISTED_GANTT_COLUMN_NAMES as readonly string[]).includes(name);
}

/** Resolve width for a column: live map → session → default → fallback. */
export function resolveGanttColumnWidth(
  name: string,
  live: Partial<Record<string, number>> | null | undefined,
  projectId: string | null | undefined,
  fallback: number,
): number {
  const fromLive = live?.[name];
  if (typeof fromLive === 'number' && fromLive > 0) return fromLive;
  if (projectId) {
    const fromSession = loadGanttColumnWidths(projectId)?.widths?.[name];
    if (typeof fromSession === 'number' && fromSession > 0) return fromSession;
  }
  const fromDefault = DEFAULT_GANTT_COLUMN_WIDTHS[name];
  if (typeof fromDefault === 'number' && fromDefault > 0) return fromDefault;
  return fallback;
}

/**
 * Apply saved widths onto the live gantt column configs (mutates column objects).
 * Prefer `applyFixedColumnWidths` when fill vs scroll mode matters.
 */
export function applyGanttColumnWidths(
  columns: Array<{ name?: string; width?: number }>,
  widths: Partial<Record<string, number>>,
): void {
  for (const col of columns) {
    const name = col.name;
    if (!name) continue;
    const w = widths[name];
    if (typeof w === 'number' && w > 0) {
      col.width = w;
    }
  }
}

/**
 * Defaults merged with live session overrides — used when restoring widths on paint.
 */
export function sessionColumnWidthMap(
  live: Partial<Record<string, number>> | null | undefined,
): Partial<Record<string, number>> {
  return {
    ...DEFAULT_GANTT_COLUMN_WIDTHS,
    ...(live ?? {}),
  };
}

/** @deprecated Use sessionColumnWidthMap */
export function pinnedGanttColumnWidthMap(
  live: Partial<Record<string, number>> | null | undefined,
): Partial<Record<string, number>> {
  return sessionColumnWidthMap(live);
}

/**
 * Apply fixed widths. In fill mode, skip the flex column so elastic can stretch it.
 * In scroll mode, pin every mapped column (including flex) so H-scroll preserves sizes.
 */
export function applyFixedColumnWidths(
  columns: Array<{ name?: string; width?: number; hide?: boolean }>,
  widths: Partial<Record<string, number>>,
  opts?: {
    flexColumn?: string;
    mode?: GridColumnLayoutMode;
  },
): void {
  const flexColumn = opts?.flexColumn ?? FLEX_GANTT_COLUMN_NAME;
  const mode = opts?.mode ?? 'fill';
  for (const col of columns) {
    if (col.hide) continue;
    const name = col.name;
    if (!name) continue;
    if (mode === 'fill' && name === flexColumn) continue;
    const w = widths[name];
    if (typeof w === 'number' && w > 0) {
      col.width = w;
    }
  }
}

/** Sum widths of visible columns (uses map when col.width is missing). */
export function sumVisibleColumnWidths(
  columns: Array<{ name?: string; width?: number; hide?: boolean }>,
  widths?: Partial<Record<string, number>>,
): number {
  let sum = 0;
  for (const col of columns) {
    if (col.hide) continue;
    const name = col.name;
    const fromCol = typeof col.width === 'number' && col.width > 0 ? col.width : 0;
    const fromMap =
      name && widths && typeof widths[name] === 'number' && (widths[name] as number) > 0
        ? (widths[name] as number)
        : 0;
    sum += fromCol > 0 ? fromCol : fromMap;
  }
  return sum;
}

/** Choose fill vs scroll from column sum vs grid panel width. */
export function chooseGridColumnLayoutMode(
  sumWidths: number,
  gridPanelWidth: number,
  slackPx: number = GRID_COLUMN_OVERFLOW_SLACK_PX,
): GridColumnLayoutMode {
  if (!(gridPanelWidth > 0) || !(sumWidths > 0)) return 'fill';
  return sumWidths > gridPanelWidth + slackPx ? 'scroll' : 'fill';
}
